import {FunctionComponent, useCallback, useEffect, useLayoutEffect, useRef} from "react";
import {MapboxCoordinates} from "../index";
import {LngLatLike, Map, Marker} from "mapbox-gl";

export interface MapboxMarker {
    id: string; // the id is used to identify the marker and move it instead of creating a new one
    center: MapboxCoordinates,
    color?: string,
    flyToOnClick?: boolean,
}

export interface MapboxMarkersProps {
    map?: Map,
    markers?: MapboxMarker[],
    markerColor?: string;
    flyToMarkerOnClick?: boolean;
}


const MapboxMarkers: FunctionComponent<MapboxMarkersProps> = (props) => {
    /**Keep track of markers so they can be removed when needed.*/
    const markersRef = useRef<Marker[]>([]);

    /**
     * Creates a marker with a specific center location in mapbox
     */
    const createMarker = useCallback((markerData: MapboxMarker, map: Map): Marker => {
        const center: LngLatLike = [markerData.center.lng, markerData.center.lat];
        const color = markerData.color ?? props.markerColor;
        const clickable = markerData.flyToOnClick ?? props.flyToMarkerOnClick;

        const marker = new Marker({color: color})
            .setLngLat(center)
            .addTo(map);
        const element = marker.getElement();
        if (element) {
            element.classList.add('mapbox-marker');
            element.classList.toggle('clickable', clickable);
            element.id = markerData.id;
            if (clickable) {
                element.addEventListener('click', () => map.flyTo({center, zoom: 15, bearing: 0, essential: true,}));
            }
        }
        return marker;
    }, [props.flyToMarkerOnClick, props.markerColor])

    /**
     * Updates a marker with a specific center location in mapbox
     */
    const updateMarker = useCallback((marker: Marker, markerData: MapboxMarker, map: Map): Marker => {
        const center: LngLatLike = [markerData.center.lng, markerData.center.lat];
        const clickable = markerData.flyToOnClick ?? props.flyToMarkerOnClick;
        const element = marker.getElement();
        if (element) {
            element.classList.toggle('clickable', clickable);
            element.id = markerData.id;
            if (clickable) {
                element.addEventListener('click', () => map.flyTo({center, zoom: 15, bearing: 0, essential: true,}));
            }
        }
        const currentCenter = marker.getLngLat();
        if (!(currentCenter.lng === center[0] && currentCenter.lat === center[1])) {
            marker.setLngLat(center);
        }
        return marker;
    }, [props.flyToMarkerOnClick])

    /**
     * Creates markers on the map using the markers prop.
     */
    const generateMarkers = useCallback((markerDataList: MapboxMarker[], map: Map) => {
        const newMarkers = [];
        // Create new markers and update existing ones
        for (const markerData of markerDataList ?? []) {
            let marker: Marker;
            const existingMarker = markersRef.current.find(m => m.getElement().id === markerData.id && m._color === markerData.color);
            if (existingMarker) {
                marker = updateMarker(existingMarker, markerData, map);
            } else {
                marker = createMarker(markerData, map);
            }
            newMarkers.push(marker);
        }
        // Remove markers that are not in the new markers list
        for (const marker of markersRef.current) {
            if (!newMarkers.includes(marker)) {
                marker.remove();
            }
        }
        markersRef.current = newMarkers;
    }, [createMarker, updateMarker])

    /**
     * Creates markers on the map using the markers prop.
     * This will remove all existing markers and create new ones.
     */
    useLayoutEffect(() => {
        if (!props.map)
            return;
        const map = props.map;
        const markerGenerator = () => generateMarkers(props.markers ?? [], map);
        if (map.isStyleLoaded()) markerGenerator();
        else map.once('style.load', markerGenerator);
        return () => {
            map.off('style.load', markerGenerator);
        }
    }, [props.map, props.markers, generateMarkers])

    /**
     * Removes all markers when the component is unmounted.
     */
    useEffect(() => () => {
        markersRef.current.forEach(m => m.remove());
    }, []);

    return null;
}

export default MapboxMarkers;